// Componente MiCuenta para usuarios autenticados
// Muestra la información del perfil y configuraciones de la cuenta
// Migrado desde la app móvil con funcionalidad de gestión de cuenta
// Rediseñado con estilo tipo settings clásico

import React from 'react';
import { FiUser, FiEdit, FiLogOut, FiMail, FiShield, FiImage, FiSettings, FiChevronRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import toast from 'react-hot-toast';

/**
 * Componente MiCuenta
 * 
 * Panel principal de cuenta para usuarios autenticados
 * Muestra información del perfil y opciones de configuración
 * Diseño tipo settings con estructura clara y jerarquía visual
 */
const MiCuenta = () => {
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const { basicInfo } = useCurrentUserProfile();

  /**
   * Abre el cliente de correo electrónico para soporte
   */
  const handleSupportPress = () => {
    const email = 'help@clubamarsi.com';
    const subject = 'Soporte/Ayuda';
    const body = 'Escribe aquí tu mensaje de soporte/ayuda';
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink, '_blank');
  };

  /**
   * Abre el navegador web con la política de privacidad
   */
  const handlePrivacyPolicyPress = () => {
    const url = 'https://clubamarsi.com/politicas-privacidad';
    window.open(url, '_blank');
  };

  /**
   * Maneja el cierre de sesión del usuario
   */
  const handleLogout = () => {
    if (window.confirm('¿Estás seguro de que quieres cerrar tu sesión?')) {
      try {
        signOut();
      } catch (error) {
        console.error('Error al cerrar sesión:', error);
        toast.error('Error al cerrar sesión');
      }
    }
  };

  /**
   * Componente de sección con título
   * Agrupa items relacionados bajo un encabezado
   */
  const Section = ({ title, children }) => (
    <div className="mb-6">
      <h3 className="text-sm font-semibold text-neutral-400 mb-2 px-4">
        {title}
      </h3>
      <div className="bg-neutral-800/40 rounded-xl overflow-hidden">
        {children}
      </div>
    </div>
  );

  /**
   * Componente de item de menú
   * Muestra una opción con icono, título y flecha de navegación
   */
  const MenuItem = ({ icon: Icon, title, subtitle, onClick, danger = false, last = false }) => (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between p-4 hover:bg-neutral-800/50 transition-colors ${!last ? 'border-b border-neutral-700/30' : ''} ${danger ? 'text-red-400' : 'text-white'}`}
    >
      <div className="flex items-center space-x-4">
        <div className={`p-2 rounded-lg ${danger ? 'bg-red-500/20' : 'bg-neutral-500/20'}`}>
          <Icon className={`h-5 w-5 ${danger ? 'text-red-400' : 'text-neutral-400'}`} />
        </div>
        <div className="text-left">
          <p className="font-medium text-sm">
            {title}
          </p>
          {subtitle && (
            <p className="text-xs text-neutral-300/70 mt-0.5">
              {subtitle}
            </p>
          )}
        </div>
      </div>
      <FiChevronRight className={`h-5 w-5 ${danger ? 'text-red-400' : 'text-neutral-400'}`} />
    </button>
  );

  return (
    <div className='max-w-2xl mx-auto p-4'>
      {/* Header con información del usuario */}
      <div className="mb-8 text-center">
        <div className="relative inline-block">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg">
            {basicInfo?.avatar_url ? (
              <img 
                src={basicInfo.avatar_url} 
                alt="Avatar" 
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <FiUser className="h-10 w-10 text-white" />
            )}
          </div>
          <div className="absolute bottom-0 right-0 w-5 h-5 bg-verde-shiny rounded-full border-2 border-neutral-900"></div>
        </div>
        <h1 className="text-2xl font-bold text-white mt-4">
          {basicInfo?.nombre || 'Mi Cuenta'}
        </h1>
        <p className="text-neutral-300 text-sm mt-1">
          {basicInfo?.email || user?.email}
        </p>
      </div>

      {/* Secciones del menú */}
      <div>
        {/* Perfil */}
        <Section title="Perfil">
          <MenuItem
            icon={FiEdit}
            title="Editar perfil"
            subtitle="Actualiza tu información personal"
            onClick={() => navigate('/mi-cuenta/editar')}
          />
          <MenuItem
            icon={FiImage}
            title="Álbum privado"
            subtitle="Gestiona tus fotos y videos"
            onClick={() => navigate('/mi-cuenta/albumes')}
            last
          />
        </Section>

        {/* Seguridad */}
        <Section title="Seguridad">
          <MenuItem
            icon={FiSettings}
            title="Gestión de cuenta"
            subtitle="Configura seguridad y accesos"
            onClick={() => navigate('/mi-cuenta/gestion')}
            last
          />
        </Section>

        {/* Ayuda */}
        <Section title="Ayuda">
          <MenuItem
            icon={FiMail}
            title="Soporte técnico"
            subtitle="Contacta a nuestro equipo"
            onClick={handleSupportPress}
          />
          <MenuItem
            icon={FiShield}
            title="Información legal"
            subtitle="Política de privacidad y términos"
            onClick={handlePrivacyPolicyPress}
            last
          />
        </Section>

        {/* Cerrar sesión */}
        <div className="mt-8">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center space-x-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl hover:bg-red-500/20 transition-colors text-red-400"
          >
            <FiLogOut className="h-5 w-5" />
            <span className="font-medium">
              Cerrar sesión
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MiCuenta;
